import Decimal from 'decimal.js'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { CalculatePanelProps } from '../../CalculationInput/CalculatePanel'
import { NumberPadActionValue } from 'components/NumberPad/NumberPad'
import { isUndefined } from 'lodash'

const floatOrZero = (num: string) => parseFloat(num) || 0

const calculatePlusOrMinus = (
    operation: string,
    total: string,
    next: string,
) => {
    if (operation === 'MINUS')
        return new Decimal(parseFloat(total))
            .minus(new Decimal(parseFloat(next)))
            .toString()
    else if (operation === 'PLUS')
        return new Decimal(parseFloat(total))
            .plus(new Decimal(parseFloat(next)))
            .toString()
    return total
}

interface UseCalculatePanelProps extends CalculatePanelProps {
    noStackQuickButton?: boolean
    maxValue?: number
    minValue?: number
}

const numberFormat = (val: number) => {
    val = !isNaN(val) ? val : 0

    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay: val === 0 ? 'never' : 'auto',
    }).format(val)
}
function useCalculatePanel(props: UseCalculatePanelProps) {
    const {
        onChange,
        quickButtons = [],
        initialValue,
        hideDot = false,
        hideQuickButtons = false,
        totalAmount,
        noStackQuickButton = false,
        maxValue,
        minValue,
    } = props

    const [next, setNext] = useState<string>('')
    const [total, setTotal] = useState<string>('')
    const [operation, setOperation] = useState<string>('')
    const [lastQuickButton, setLastQuickButton] = useState('')
    const [hasInitialValueSet, setHasInitialValueSet] = useState<boolean>(false)
    const handleChange = useCallback(
        (total: string) => {
            onChange(
                new Decimal(floatOrZero(total)).toDecimalPlaces(2).toNumber(),
            )
        },
        [onChange],
    )

    const onOperation = useCallback(
        (_operation: string) => {
            if (total === '' || total === '0') return
            if (next && operation) {
                setTotal((old) => calculatePlusOrMinus(operation, old, next))
                setNext('')
            }
            setOperation(_operation)
        },
        [next, operation, total],
    )
    const calculate = useCallback(() => {
        if (next && operation)
            setTotal((old) => calculatePlusOrMinus(operation, old, next))
        setNext('')
        setOperation('')
        handleChange(calculatePlusOrMinus(operation, total, next))
    }, [handleChange, next, operation, total])

    const onClear = useCallback(() => {
        setNext('')
        setTotal('')
        setOperation('')
        handleChange('')
    }, [handleChange])

    const onBack = useCallback(() => {
        if (next !== '') {
            setNext((old) => old.replace(/.$/, ''))
        } else if (operation !== '') {
            setOperation('')
        } else {
            setTotal((old) => old.replace(/.$/, ''))
            handleChange(total.replace(/.$/, ''))
        }
    }, [handleChange, next, operation, total])

    const onClickDot = useCallback(() => {
        if (operation || next) {
            if (next.includes('.')) return
            setNext((n) => {
                if (!n) return '0.'
                return n + '.'
            })
        } else {
            if (total.includes('.')) return
            setTotal((old) => {
                if (!old) return '0.'
                return old + '.'
            })
        }
    }, [operation, next, total])

    const onClickNumPad = useCallback(
        (val: number | NumberPadActionValue, isPreset: boolean = false) => {
            if (val === 'BACK') onBack()
            else if (val === 'CLEAR') onClear()
            else if (val === 'DOT') onClickDot()
            else if (val === 'MINUS' || val === 'PLUS') onOperation(val)
            else if (val === 'EQUAL') calculate()
            else if (isPreset) {
                // '{number}%'
                var newValue = val.toString().replace('%', '')
                handleChange(newValue)
            } else if (typeof val === 'number') {
                let newtotal = total
                if (newtotal === '0') newtotal = ''
                if (val === 0 && total === '') {
                    return setTotal('')
                }
                if (operation) {
                    if (val === 0 && next === '0') return
                    setNext((old) => old + val)
                } else {
                    if (/(\.[0-9]{2}$)/.test(newtotal)) return
                    if (lastQuickButton !== '') {
                        setTotal(val.toString())
                        handleChange(val.toString())
                    } else {
                        setTotal((old) => old + val)
                        handleChange(newtotal + val)
                    }
                }
                setLastQuickButton('')
            }
        },
        [
            total,
            operation,
            handleChange,
            next,
            onBack,
            onClear,
            calculate,
            onOperation,
            onClickDot,
            lastQuickButton,
        ],
    )

    const quickButtonFormat: string[] = useMemo(() => {
        return quickButtons
            .filter((number) => number !== 0)
            .map((number: number) => {
                return number.toFixed(4).replace(/[.]?0+$/, '')
            })
    }, [quickButtons])

    const onClickQuickButton = useCallback(
        (val: string) => {
            var numVal = floatOrZero(val)

            if (total === '' || val === quickButtonFormat[0]) {
                setTotal(val)
                handleChange(val)
            } else if (
                lastQuickButton !== '' &&
                lastQuickButton !== quickButtonFormat[0] &&
                !noStackQuickButton
            ) {
                setTotal((old) => (floatOrZero(old) + numVal).toString())
                handleChange((floatOrZero(total) + numVal).toString())
            } else {
                setTotal(val)
                handleChange(val)
            }
            setNext('')
            setOperation('')
            setLastQuickButton(val)
        },
        [
            total,
            quickButtonFormat,
            lastQuickButton,
            noStackQuickButton,
            handleChange,
        ],
    )

    useEffect(() => {
        if (initialValue || initialValue === 0) setHasInitialValueSet(true)
    }, [initialValue])
    useEffect(() => {
        if (hasInitialValueSet) {
            setTotal((initialValue ?? 0).toString())
            handleChange((initialValue ?? 0).toString())
            setHasInitialValueSet(false)
        }
    }, [handleChange, initialValue, hasInitialValueSet])

    useEffect(() => {
        if ((!isUndefined(maxValue) || !isUndefined(minValue)) && total) {
            if (!isUndefined(maxValue) && parseFloat(total) > maxValue) {
                setTotal(maxValue.toString())
                handleChange(maxValue.toString())
            } else if (!isUndefined(minValue) && parseFloat(total) < minValue) {
                setTotal(minValue.toString())
                handleChange(minValue.toString())
            }
        }
    }, [handleChange, maxValue, minValue, total])
    const text = useMemo(
        () =>
            `${numberFormat(parseFloat(total))} ${
                // operation : 'PLUS' + or 'MINUS' - or '' no operation
                operation === 'PLUS' ? '+' : operation === 'MINUS' ? '-' : ''
            } ${next ? numberFormat(parseFloat(next)) : ''}`.trim(),
        [total, next, operation],
    )
    return {
        text,
        total: floatOrZero(total),
        quickButtonFormat,
        onClear,
        onClickNumPad,
        onClickQuickButton,
        setTotal,
        hideDot,
        totalAmount,
        hideQuickButtons,
    }
}
export default useCalculatePanel
